import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase";

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[character]));

const formatTime = (value) =>
  value?.toDate
    ? value.toDate().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      })
    : "Time unavailable";

export default function WorksheetRequestConversation({
  request,
  viewerRole,
  disabled = false,
}) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [feedback, setFeedback] = useState("");

  const sendingRef = useRef(false);

  const isTeacher = viewerRole === "teacher";
  const active = ["Open", "Awaiting Information"].includes(request.status);

  const inputId = `request-message-${request.id}-${viewerRole}`;

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    setMessages([]);
    setDraft("");
    setFeedback("");

    const messagesQuery = query(
      collection(db, "Worksheet_Requests", request.id, "messages"),
      orderBy("createdAt", "asc")
    );

    return onSnapshot(
      messagesQuery,
      (snapshot) => {
        setMessages(
          snapshot.docs.map((messageDoc) => ({
            ...messageDoc.data(),
            id: messageDoc.id,
          }))
        );
        setLoading(false);
        setLoadError("");
      },
      (error) => {
        console.error("Unable to load request conversation:", error);
        setLoadError("Unable to load the conversation. Please refresh.");
        setLoading(false);
      }
    );
  }, [request.id]);

  const sendMessage = async () => {
    if (sendingRef.current || disabled || !active || loading || loadError) {
      return;
    }

    const text = draft.trim();

    if (!text || text.length > 20000) {
      setFeedback("Enter a message between 1 and 20,000 characters.");
      return;
    }

    const user = auth.currentUser;

    if (!user) {
      setFeedback("Please sign in again.");
      return;
    }

    sendingRef.current = true;
    setSending(true);
    setFeedback("");

    const requestRef = doc(db, "Worksheet_Requests", request.id);
    const messagesRef = collection(requestRef, "messages");
    const messageRef = doc(messagesRef);

    try {
      /*
       * Save the message and update the request status atomically.
       * Also preserve the existing legacy question on the first message.
       */
      const recipient = await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(requestRef);

        if (!snapshot.exists()) {
          throw new Error("This request no longer exists.");
        }

        const current = snapshot.data();

        const expectedSenderUid = isTeacher
          ? current.requestedTeacherId
          : current.uid;

        if (expectedSenderUid !== user.uid) {
          throw new Error("You cannot send messages on this request.");
        }

        if (!["Open", "Awaiting Information"].includes(current.status)) {
          throw new Error(
            "This request is closed. Its conversation remains available to read."
          );
        }

        if (current.actionInProgress) {
          throw new Error(
            "The worksheet request is being updated. Please try again shortly."
          );
        }

        const recipientUid = isTeacher
          ? current.uid
          : current.requestedTeacherId;

        if (!recipientUid) {
          throw new Error("The message recipient is missing.");
        }

        const legacyText =
          typeof current.informationRequested === "string"
            ? current.informationRequested.trim()
            : "";

        if (!current.conversationStarted && legacyText) {
          transaction.set(doc(messagesRef, "legacy-information"), {
            senderUid:
              current.informationRequestedBy ||
              current.requestedTeacherId,
            senderRole: "teacher",
            text: legacyText,
            createdAt: current.informationRequestedAt || null,
            legacy: true,
          });
        }

        transaction.set(messageRef, {
          senderUid: user.uid,
          senderRole: viewerRole,
          text,
          createdAt: serverTimestamp(),
          emailNotificationStatus: "Pending",
        });

        transaction.update(requestRef, {
          conversationStarted: true,
          status: isTeacher ? "Awaiting Information" : "Open",
          awaitingInformation: isTeacher,
          lastMessageAt: serverTimestamp(),
          lastMessageBy: user.uid,
          lastMessageRole: viewerRole,
          resolution: isTeacher
            ? "The teacher has requested information. See the conversation."
            : "The requester has replied. See the conversation.",
          updatedAt: serverTimestamp(),
        });

        return {
          uid: recipientUid,
          reference: current.caseId || request.id,
        };
      });

      // The message is now saved, regardless of email outcome.
      setDraft("");

      let emailSent = false;

      try {
        const recipientProfile = await getDoc(
          doc(db, "profiles", recipient.uid)
        );

        const rawEmail = recipientProfile.exists()
          ? recipientProfile.data().email
          : "";

        const email = typeof rawEmail === "string" ? rawEmail.trim() : "";

        if (!/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(email)) {
          throw new Error("Recipient email is unavailable.");
        }

        const notificationText = isTeacher
          ? `You have a new message about worksheet request ${recipient.reference}. ` +
            "Please sign in to Eascode, open Request Worksheet, " +
            "and reply in the conversation under Your Requests."
          : `You have a new reply about worksheet request ${recipient.reference}. ` +
            "Please sign in to Eascode, open Your Alerts, " +
            "and view the conversation under Worksheet requests for you.";

        const sendEmail = httpsCallable(functions, "sendWorksheetEmail");

        await sendEmail({
          friendName: isTeacher ? "Worksheet requester" : "Teacher",
          friendEmail: email,
          worksheetTitle: escapeHtml(
            `New message — ${recipient.reference}`
          ),
          message: escapeHtml(notificationText),
        });

        emailSent = true;
      } catch (error) {
        console.error("Message saved; email not confirmed:", error);
      }

      try {
        await updateDoc(messageRef, {
          emailNotificationStatus: emailSent ? "Sent" : "Not confirmed",
          ...(emailSent
            ? { emailNotificationSentAt: serverTimestamp() }
            : {}),
        });
      } catch (error) {
        console.error("Unable to save email notification status:", error);
      }

      setFeedback(
        emailSent
          ? "Message saved and email notification sent."
          : "Message saved, but email notification could not be confirmed. Do not resend the message."
      );
    } catch (error) {
      console.error("Unable to send request message:", error);
      setFeedback(
        `${error.message || "Unable to confirm the message was saved."} ` +
        "Check the conversation before trying again."
      );
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  /*
   * Show the older informationRequested field until it has been
   * copied into the permanent message history.
   */
  const legacyQuestion =
    !request.conversationStarted && request.informationRequested;

  const controlsDisabled =
    disabled ||
    sending ||
    loading ||
    Boolean(loadError) ||
    Boolean(request.actionInProgress);

  const renderMessage = (message, key) => (
    <div
      key={key}
      style={{
        minWidth: 0,
        padding: "12px",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        backgroundColor:
          message.senderRole === "teacher" ? "#eff6ff" : "#f8fafc",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: "6px",
          fontSize: "12px",
          color: "#475569",
        }}
      >
        <strong>
          {message.senderRole === "teacher" ? "Teacher" : "Requester"}
          {message.legacy ? " · Earlier question" : ""}
        </strong>

        <span>{formatTime(message.createdAt)} IST</span>
      </div>

      <p
        style={{
          margin: "8px 0 0",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          fontSize: "14px",
          lineHeight: 1.6,
          color: "#334155",
        }}
      >
        {message.text}
      </p>
    </div>
  );

  return (
    <section
      aria-label="Request conversation"
      style={{
        minWidth: 0,
        marginTop: "20px",
        paddingTop: "16px",
        borderTop: "1px solid #e2e8f0",
      }}
    >

<details>
  <summary
    style={{
      padding: "10px 0",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: 700,
      color: "#0f172a",
      overflowWrap: "anywhere",
    }}
  >
    Conversation
    {!loading && !loadError &&
      ` (${messages.length + (
        legacyQuestion &&
        !messages.some((message) => message.id === "legacy-information")
          ? 1
          : 0
      )})`}
    {!active ? " · Closed" : ""}
  </summary>

  <div style={{ marginTop: "12px" }}>

      {loading ? (
        <p role="status">Loading conversation...</p>
      ) : loadError ? (
        <p role="alert" style={{ color: "#b91c1c" }}>
          {loadError}
        </p>
      ) : (
        <div style={{ display: "grid", gap: "10px" }}>
          {legacyQuestion &&
            !messages.some((message) => message.id === "legacy-information") &&
            renderMessage(
              {
                senderRole: "teacher",
                text: legacyQuestion,
                createdAt: request.informationRequestedAt,
                legacy: true,
              },
              "legacy-preview"
            )}

          {messages.map((message) =>
            renderMessage(message, message.id)
          )}

          {!legacyQuestion && messages.length === 0 && (
            <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>
              No messages yet.
            </p>
          )}
        </div>
      )}

      {active ? (
        <div style={{ marginTop: "16px" }}>
          <label
            htmlFor={inputId}
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: 600,
              color: "#334155",
            }}
          >
            {isTeacher ? "Ask the requester for information" : "Your reply"}
          </label>

          <textarea
            id={inputId}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={20000}
            disabled={controlsDisabled}
            placeholder={
              isTeacher
                ? "What information do you need?"
                : "Enter your response to the teacher..."
            }
            style={{
              display: "block",
              width: "100%",
              maxWidth: "100%",
              minHeight: "110px",
              boxSizing: "border-box",
              padding: "12px",
              border: "1px solid #cbd5e1",
              borderRadius: "10px",
              fontFamily: "inherit",
              fontSize: "16px",
              color: "#0f172a",
              backgroundColor: "#ffffff",
              resize: "vertical",
            }}
          />

          <button
            type="button"
            onClick={sendMessage}
            disabled={controlsDisabled || !draft.trim()}
            style={{
              minHeight: "44px",
              maxWidth: "100%",
              marginTop: "10px",
              padding: "10px 16px",
              border: "none",
              borderRadius: "10px",
              backgroundColor: "#2563eb",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: 600,
              opacity: controlsDisabled || !draft.trim() ? 0.55 : 1,
              cursor: controlsDisabled ? "wait" : "pointer",
            }}
          >
            {sending
              ? "Sending..."
              : isTeacher
                ? "Send question"
                : "Send reply"}
          </button>
        </div>
      ) : (
        <p style={{ marginTop: "12px", fontSize: "13px", color: "#64748b" }}>
          This request is closed. The conversation is preserved for reference.
        </p>
      )}

      {feedback && (
        <p
          role="status"
          style={{
            marginTop: "10px",
            fontSize: "13px",
            color: "#475569",
            overflowWrap: "anywhere",
          }}
        >
          {feedback}
        </p>
      )}
        </div>
</details>
    </section>
  );
}